// First-login walkthroughs. Steps anchor to [data-tour=...] attributes on
// existing elements; steps without a target show a centered card.

export interface TourStep {
  /** Navigate here before showing the step (optional). */
  route?: string
  /** CSS selector, usually [data-tour="..."] (optional → centered card). */
  target?: string
  title: string
  body: string
}

export const coachTour: TourStep[] = [
  {
    title: 'Welcome, Coach!',
    body: "Here's a quick tour of where everything lives — scheduling, lineups, stats, and awards. You can skip anytime; an admin can replay it for you later.",
  },
  {
    route: '/dashboard',
    target: '[data-tour="team-switcher"]',
    title: 'Your team',
    body: 'This is your team hub. If you coach or play on more than one team, hover here to switch between them — everything on screen follows the active team.',
  },
  {
    route: '__team__',
    target: '[data-tour="tab-schedule"]',
    title: 'Schedule',
    body: 'Games live here in a list or a month calendar (toggle at the top-left). On the calendar, click any date to schedule a game with that date pre-filled — just set the time and opponent.',
  },
  {
    route: '__team__',
    target: '[data-tour="add-game"]',
    title: 'Adding games',
    body: "When adding a game, pick whether you're the home or away team — the opponent takes the other side. Manage your opponents (name-only rival teams) under Manage → Opponents.",
  },
  {
    route: '__team__',
    target: '[data-tour="tab-lineup"]',
    title: 'Lineups',
    body: 'Build a batting order in the Lineup Builder: pick who\'s playing, hit Suggest Lineup for a stat-driven order (add notes like "Nick bats last" and the AI honors them), then drag to fine-tune, Save, and Publish so players can see it.',
  },
  {
    route: '__team__',
    target: '[data-tour="record-stats"]',
    title: 'Recording stats',
    body: 'After the game, Record Stats opens a spreadsheet — one row per player, type across the columns and save. Heads up: later corrections happen on the Stats page (top menu), filtered Team → Season → League → Game.',
  },
  {
    route: '__team__',
    target: '[data-tour="tab-stats"]',
    title: 'Team stats',
    body: "Season numbers for every player — games, hits, AVG, OPS — sorted by OPS. Your players see this too (read-only), plus their own My Stats page.",
  },
  {
    route: '__team__',
    target: '[data-tour="awards"]',
    title: 'Awards',
    body: 'After each week\'s games, pick a Player of the Week — the app suggests the top performers from the last two games and writes a fun AI summary. Crown a Season MVP when the season wraps.',
  },
  {
    target: '[data-tour="manage-menu"]',
    title: 'The Manage menu',
    body: 'Everything else is here: invite players (or direct-add existing accounts), manage opponents, and send AI-written Coach Jeter recap emails to your players. Enjoy the season!',
  },
]

export const playerTour: TourStep[] = [
  {
    title: 'Welcome to Softball Stats Club!',
    body: "Here's a quick tour of where to find your stats, your team's schedule, and the lineup. You can skip anytime.",
  },
  {
    route: '/my-stats',
    target: '[data-tour="my-stats-table"]',
    title: 'Your stats',
    body: 'My Stats shows your batting line for every team you play on — at-bats, hits, AVG, OBP, SLG, OPS — plus the full team table. Click any column header to sort, and use "Exclude subs" to filter fill-in players.',
  },
  {
    target: '[data-tour="team-switcher"]',
    title: 'Your team page',
    body: 'This takes you to your team — schedule, published lineups, team stats, and the roster. On multiple teams? Hover to switch.',
  },
  {
    route: '__team__',
    target: '[data-tour="tab-lineup"]',
    title: 'The lineup',
    body: "Once your coach publishes a lineup, it appears here — your batting spot and position for the next game. Doubleheader? Use the dropdown to flip between games.",
  },
  {
    route: '__team__',
    target: '[data-tour="tab-schedule"]',
    title: 'Schedule & results',
    body: 'Upcoming games with times and venues, and completed games with the score and a W or L. Check the team page header for the season record. Play ball!',
  },
]
